import SingleSelectorDialogBox from '../../Selectors/SingleSelectorDialogBox/SingleSelectorDialogBox'
import getCMResponse from '../../../utils/api_manager';
import styles from './GoogleAdsAuth.module.css';
import GoogleAdsIcon from '../../../assets/icons/google-ads.svg';
import GoogleDataAccess from '../GoogleDataAccess/GoogleDataAccess';
import { useState, useCallback } from 'react';

const GoogleAdsAuth = ({
    grantedScopes = [],
    requiredScopes = [],
    onGrantedScopesUpdate,
    project,
    providerProjectId,
    onLoginReady,
    // Grant-access loader centralization (requirement 3): raised across the
    // OAuth-popup-close -> account-list-fetch gap, any MCC drill-down fetch,
    // and the final account-save call. Optional -- only GrantOnboardingAccess
    // supplies it today.
    onLoadingChange,
}) => {
    
    const [ showAccountSelector, setShowAccountSelector ] = useState(false);
    const [ userAccounts, setUserAccounts ] = useState([]);
    const [ accessDetails, setAccessDetails ] = useState(null);
    // google-ads-mcc-support TRD, Section 3 R2: tracks an in-progress MCC
    // drill-down, separate from the original (non-MCC) selector's state.
    // null when inactive; otherwise { topLevelMccId, currentManagerId,
    // options, loading, errorMessage }.
    const [ mccDialog, setMccDialog ] = useState(null);

    // Check if all scopes are granted and account is selected
    const missingScopes = requiredScopes.filter((scope) => !grantedScopes.includes(scope));
    const allScopesGranted = missingScopes.length === 0;
    const isCompleted = allScopesGranted && providerProjectId != null;

    // google-ads-mcc-support TRD, Section 3 R3/R4: fetches one manager
    // account's direct client accounts and (re)populates mccDialog with
    // the result. Shared by the initial MCC detection (R3) and every
    // recursive drill-down level (R4) -- the only thing that changes
    // between calls is managerCustomerId; topLevelMccId is fixed for the
    // whole drill-down sequence.
    const fetchClientAccounts = useCallback((topLevelMccId, managerCustomerId) => {
        setMccDialog({
            topLevelMccId,
            currentManagerId: managerCustomerId,
            options: [],
            loading: true,
            errorMessage: null,
        });
        onLoadingChange?.(true);
        getCMResponse('/datasource/google-ads/list-client-accounts', 'GET', {
            project_id: project.id,
            manager_customer_id: managerCustomerId,
            top_level_mcc_id: topLevelMccId,
        }).then((response) => {
            const formattedAccounts = (response.accounts || []).map((account) => ({
                id: account.id,
                displayName: account.descriptiveName,
                manager: account.manager === true,
            }));
            setMccDialog({
                topLevelMccId,
                currentManagerId: managerCustomerId,
                options: formattedAccounts,
                loading: false,
                errorMessage: null,
            });
        }).catch((error) => {
            console.error('Error fetching Google Ads client accounts:', error);
            setMccDialog({
                topLevelMccId,
                currentManagerId: managerCustomerId,
                options: [],
                loading: false,
                errorMessage: "We couldn't load the accounts under this Manager Account. Please try again.",
            });
        }).finally(() => {
            onLoadingChange?.(false);
        });
    }, [project, onLoadingChange]);

    // google-ads-mcc-support TRD, Section 3 R5: replicates the existing
    // (pre-feature) cancel semantics exactly -- clears local state only,
    // no rollback call, no partial write of any intermediate manager id.
    const handleMccCancel = useCallback(() => {
        setMccDialog(null);
        setShowAccountSelector(false);
    }, []);

    const handleAccountSelect = useCallback((account) => {
        // console.log('Selected Google Ads Account:', account);
        // google-ads-mcc-support TRD, Section 3 R3: branch on the manager
        // flag surfaced by handleAuthCallback below. Non-MCC path is
        // byte-for-byte unchanged from before this feature.
        if (account.manager === true) {
            fetchClientAccounts(account.id, account.id);
            return;
        }
        onLoadingChange?.(true);
        getCMResponse('/datasource/update-datasource-details', 'POST', {
            access_id: accessDetails.id,
            property_id: account.id,
        }).then((response) => {
            // console.log('Google Ads property set successfully:', response);
            setShowAccountSelector(false);
            onGrantedScopesUpdate();
        }).catch((error) => {
            console.error('Error setting Google Ads property:', error);
        }).finally(() => {
            onLoadingChange?.(false);
        });
    }, [onGrantedScopesUpdate, accessDetails, fetchClientAccounts, onLoadingChange]);

    // google-ads-mcc-support TRD, Section 3 R4: handles a selection made
    // from the MCC drill-down dialog itself (as opposed to the original,
    // non-MCC dropdown handled by handleAccountSelect above).
    const handleMccOptionSelect = useCallback((childAccount) => {
        if (!mccDialog) {
            return;
        }
        if (childAccount.manager === true) {
            // Drill down again: topLevelMccId stays fixed, only the
            // manager being queried changes.
            fetchClientAccounts(mccDialog.topLevelMccId, childAccount.id);
            return;
        }
        // Leaf reached -- complete the connection against the original
        // top-level MCC id (provider_project_id) plus this leaf's id
        // (provider_metadata.account_id).
        onLoadingChange?.(true);
        getCMResponse('/datasource/update-datasource-details', 'POST', {
            access_id: accessDetails.id,
            property_id: mccDialog.topLevelMccId,
            account_id: childAccount.id,
        }).then(() => {
            setMccDialog(null);
            onGrantedScopesUpdate();
        }).catch((error) => {
            console.error('Error setting Google Ads client account:', error);
        }).finally(() => {
            onLoadingChange?.(false);
        });
    }, [mccDialog, accessDetails, onGrantedScopesUpdate, fetchClientAccounts, onLoadingChange]);

    const handleAuthCallback = useCallback((accessDetailsResponse) => {
        // onLoadingChange(true) was already raised by GoogleDataAccess when
        // the OAuth popup resolved -- this call is the second leg of that
        // span (account list fetch), so it clears it here.
        setAccessDetails(accessDetailsResponse);
        console.log('Google Ads authentication successful');
        getCMResponse('/datasource/google-ads/get-user-accounts', 'GET', {
            project_id: project.id,
        }).then((response) => {
            // console.log('Google Ads auth completed:', response);
            var formattedAccounts = (response.accounts || []).map((account) => ({
                id: account.id,
                displayName: account.descriptiveName,
                manager: account.manager === true,
            }));
            setUserAccounts(formattedAccounts);
            setShowAccountSelector(true);
        })
        .catch((error) => {
            console.error('Error completing Google Ads auth:', error);
        })
        .finally(() => {
            onLoadingChange?.(false);
        });
    }, [project, onLoadingChange]);

    // If completed, show the icon but make it unclickable. Bug fix:
    // datasource-reconnect-menu-noop/trd.md, Requirement 2 (Gap B) --
    // GoogleDataAccess must stay mounted even once connected (it already
    // renders null of its own accord in managed mode, so this causes no
    // visual change) so its unconditional onLoginReady effect fires and
    // loginRefs.current['GOOGLEADS'] gets populated, matching
    // MetaAuth/GoogleMerchantCenterAuth's pattern.
    if (isCompleted) {
        return (
            <div className={styles.container}>
                <img
                    src={GoogleAdsIcon}
                    alt="Google Ads - Connected"
                    className={`${styles.iconButton} ${styles.completed}`}
                />
                <GoogleDataAccess
                    requiredScopes={requiredScopes}
                    onAccessGrant={handleAuthCallback}
                    icon={GoogleAdsIcon}
                    project={project}
                    provider="GOOGLEADS"
                    onLoginReady={onLoginReady}
                    onLoadingChange={onLoadingChange}
                />
            </div>
        );
    }
    
    return (
        <div className={styles.container}>
            {
                !showAccountSelector ?
                    <GoogleDataAccess
                        requiredScopes={requiredScopes}
                        onAccessGrant={handleAuthCallback}
                        icon={GoogleAdsIcon}
                        project={project}
                        provider="GOOGLEADS"
                        onLoginReady={onLoginReady}
                        onLoadingChange={onLoadingChange}
                    /> : (
                        mccDialog ?
                            // google-ads-mcc-support TRD, Section 3 R3/R4/R6:
                            // the selected account (or a subsequent drill-down
                            // selection) turned out to be a Manager Account --
                            // show its direct client accounts instead of the
                            // original dropdown until a leaf account is reached.
                            <SingleSelectorDialogBox
                                listOptions={mccDialog.options}
                                handleOptionSelect={handleMccOptionSelect}
                                setShowSelector={handleMccCancel}
                                primaryText="The selected account is a Manager Account (MCC). Choose a client account underneath it to continue."
                                ctaText="Select"
                                optionIcon={GoogleAdsIcon}
                                dialogTitle="Manager Account Detected"
                                errorMessage={mccDialog.errorMessage}
                            /> :
                            <SingleSelectorDialogBox
                                listOptions={userAccounts}
                                handleOptionSelect={handleAccountSelect}
                                setShowSelector={setShowAccountSelector}
                                primaryText="Select an account"
                                ctaText="Select"
                                optionIcon={GoogleAdsIcon}
                                dialogTitle="Select Google Ads Account"
                            />
                    )
            }
        </div>
    );
}

export default GoogleAdsAuth;

